import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled'
import { mediaQueries } from 'styled/core'

import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { actionCreators, GettyActionCreators } from '../../../actions'

import { GettyStore } from '../../../store'

import { stopSchema } from '../../../schemas/stop'

import { connect } from 'containers/store'
import { sharedActionCreators } from 'containers/shared'

import { Fieldset } from 'components/ui/fieldset'
import { FooterEditEntity, FOOTER_HEIGHT } from 'components/ui/footer'
import { NAVBAR_HEIGHT } from 'components/ui/primary-navigation'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { StatusGuard } from 'components/ui/status-guard'
import { MediaField } from 'domains/media/components/media-field'
import { LayoutFullWidthPage } from 'layouts/full-width-page'
import { Description, Image, Title, Subtitle } from 'components/ui/list-item-label'

import { GetStopRequest } from 'generated/mos/org/getty/audioaccess'
import { Stop } from 'generated/mos/org/getty/audio'
import { Media } from 'generated/mos/media'
import { UpdateStopRequest } from 'generated/mos/org/getty/audiomanagement'
import { Language } from 'generated/mos/i18n'

import { bffEndpoint } from 'helpers/bff'
import { pick, areRefValuesEqual } from 'helpers/core'
import { statusSelector, Status } from 'helpers/status'
import { space } from 'helpers/style'
import { translateValues, TranslationFormBags } from 'helpers/schema'
import { defaultLanguage, isDefaultLanguage, parseLanguage } from 'helpers/i18n'
import { getResolvedValue } from 'helpers/linkables'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { getStopSubnav } from '../subnav'

import { LANGUAGES, LanguageCode } from '../../../constants'
import { Input } from 'components/ui/input'

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetStopRequest' | 'gettyUpdateStopRequest' | 'gettyUpdateStopReset'> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyStore['getty'], 'stopUpdate'>;

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly stopId: string;
  readonly tourId?: string;
};

type Props = ActionProps & ConnectedProps & DirectProps;

type FormValues = {
  title: string | undefined;
  objectDescription: string | undefined;
  audio: Media.Ref | undefined;
}

type FormProps = {
  language: Language.Entity;
  formBag: FormBag<FormValues> | undefined;
  setFormBag: (bag: FormBag<FormValues>) => void;
}

const HEADER_HEIGHT = 100

const Scroll = styled.div`
  display: flex;
  padding-top: ${space(6)};
  padding-bottom: ${space(15)};
  overflow-x: scroll;
  height: calc(100vh - ${(NAVBAR_HEIGHT + FOOTER_HEIGHT + HEADER_HEIGHT)}px);

`
const Panels = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  padding-left: ${space(9)};
  padding-right: ${space(9)};


  ${mediaQueries.lap} {
    padding-left: ${space(18)};
    padding-right: ${space(18)};
  }

  @media only screen and (min-width: 1600px) {
    padding-left: calc(${space(18)} + (100% - 1600px) / 2);
    padding-right: calc(${space(18)} + (100% - 1600px) / 2);
  }
`

const Panel = styled.div`
  width: calc(100vw - 36px);
  flex: 0 0 auto;
  &:not(:last-child) {
    padding-right: ${space(6)};
    margin-right: ${space(6)};
    border-right: 1px solid ${({ theme }) => theme.color.grayL60};
  }

  ${mediaQueries.hand} {
    width: 500px;
  }
`

const StopItem = styled.div`
  display: flex;
  width: 100%;
  height: ${HEADER_HEIGHT}px;
  align-items: center;
  padding-top: ${space(8)};
  padding-bottom: ${space(6)};
  border-bottom: 1px solid ${({ theme }) => theme.color.grayL60};
`

const TranslationForm = (props: FormProps) => {
  const { formBag, language, setFormBag } = props
  
  const updateFormBagAudio = (entity: Media.Entity | undefined) => {
    if(formBag) {
      setFormBag({
        ...formBag,
        values: {
          ...formBag.values,
          audio: entity && entity.ref || undefined
        }
      })
    }
  }

  return formBag ? (
    <Panel>
      <FormData
        bag={formBag}
        onUpdate={(event) => {
          setFormBag(event.bag)
        }}
        validate={() => ({})}
        >
        <Fieldset legend={parseLanguage(language)}>
          <Input
            label="Stop Title"
            field="title"
            isDisabled={isDefaultLanguage(language)}
          />

          <Input
            label="Description"
            field="objectDescription"
            component="textarea"
            isDisabled={isDefaultLanguage(language)}
          />
          <MediaField
            kind="MEDIA_KIND_AUDIO"
            mediaRef={formBag.values.audio}
            onUpdate={(entity) => updateFormBagAudio(entity)}
            label="Audio"
            language={language}
            isDisabled={isDefaultLanguage(language)}
          />
        </Fieldset>
      </FormData>
    </Panel>
  ) : null
}

const StopTranslationsView = (props: Props) => {
  const {
    gettyGetStopRequest ,
    gettyUpdateStopRequest,
    historyPush,
    projectId,
    stopId,
    stopUpdate,
    toastNotification,
    tourId,
  } = props;

  // set up form values
  const [ initialValues, setInitialValues ] = useState<TranslationFormBags<FormValues, LanguageCode> | undefined>(undefined);
  const [ formBags, setFormBags ] = useState<TranslationFormBags<FormValues, LanguageCode> | undefined>(undefined);

  const backLocation = tourId ? `/project/${projectId}/tour/${tourId}/stops` : `/project/${projectId}/stops`

  const stopUpdateData = statusSelector.data(stopUpdate);
  const stop = stopUpdateData && stopUpdateData.stop;
  const resolvedValues = stopUpdateData && stopUpdateData.resolvedValues;

  const hasSubmitted = useRef(false)
  const hostName = bffEndpoint(window.location.hostname)

  // load initial data
  useEffect(() => {
    if (stopUpdate.status === Status.Idle || (stop && Stop.mustRef(stop.ref).id !== stopId)) {
      gettyGetStopRequest({
        type: GetStopRequest.refName,
        stopRef: {
          typename: 'mos.org.getty.audio.Stop',
          id: stopId,
        }
      })
    }
  })


  useEffect(() => {
    
    // only proceed if stop data is available and status is ready
    if (!stop || stopUpdate.status !== Status.Ready) return;

    const getInitialValues = () => {
      const initialValues = LANGUAGES.reduce(
        (acc, curr) => {

          const audio = stopSchema.audio.read(stop.audio, curr) as Media.Ref | undefined          
          const title = stop.title ? getResolvedValue(stopSchema.title.read(stop.title, curr), resolvedValues || []) : ''
          const objectDescription = stop.objectDescription ? getResolvedValue(stopSchema.objectDescription.read(stop.objectDescription, curr), resolvedValues || []) : ''
          
          return {
            ...acc,
            [curr.languageCode]: createFormBag({
              audio,
              title,
              objectDescription,
            })
          }
        // eslint-disable-next-line @typescript-eslint/no-object-literal-type-assertion
        }, {} as TranslationFormBags<FormValues, LanguageCode>)
      return initialValues
    }
    
    const initValues = getInitialValues()
    setInitialValues(initValues)
    setFormBags(initValues)

  }, [stop, stopUpdate.status]) // eslint-disable-line react-hooks/exhaustive-deps
  

  // if the data has been submitted, trigger success toast.
  useEffect(() => {
    if (stopUpdate.status === Status.Ready && hasSubmitted.current) {
      toastNotification({ type: 'success', text: 'Translations updated' })
      hasSubmitted.current = false
    }
  },[hasSubmitted, stopUpdate.status, toastNotification])

  /**
   * Determine if current form state is dirty
   * Iterate through through the form values for each translation language
   * and return true as soon as it finds a value that has been changed
   * @returns boolean
   */
  const areValuesModified = () => {
    // don't continue unless initial values and formbag values exist
    if (!initialValues || !formBags) return;

    for (const value of LANGUAGES) {
      const languageCode: LanguageCode = value.languageCode as LanguageCode
      
      const values = { 
        initial: {...initialValues[languageCode].values},
        current: {...formBags[languageCode].values}
      }
      
      if ( 
        !areRefValuesEqual(values.initial.audio, values.current.audio) || 
        values.initial.title !== values.current.title ||
        values.initial.objectDescription !== values.current.objectDescription
      ) return true;
    }

    return false;
  }
  
  const modified = areValuesModified()


  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {}
    return errors
  }

  const validate = (formBags: TranslationFormBags<FormValues, LanguageCode>): boolean => {
    let valid = true;
    LANGUAGES.forEach((value) => {
      const languageCode: LanguageCode = value.languageCode as LanguageCode
      const bag = validateFormBag(formBags[languageCode], validateForm);
      valid = bag.valid
      updateFormBags(languageCode as LanguageCode)(bag)
    })
    return valid
  }

  /**
   * The default language values for each field are resolved to string in the form fields
   * but they could really be be entity references. Before saving updated translation strings
   * we need to copy the original values for the default language back into the array 
   * of translated values so that we don't accidentally overwrite entity reference
   * values with resolved strings in the form data
   * @param field name of the field to operate on
   * @param translatedValues the function returned by translateValues for field lookup
   * @param stop Stop.Entity which includes the original values we need to retrieve
   * @returns 
   */
  const insertOriginalDefaultLanguageValues = <K extends keyof Stop.Entity>(
    field: K, 
    translatedValues: (key: K) => any[],
    stop: Stop.Entity
  ) => {
    
    
    // Look in the raw stop data received from the server to
    // get the default language's unmodified  values for the field
    const defaultLanguageValues = stopSchema[field].read(stop[field], defaultLanguage());
    
    // get an array of translated values for the specific field we're interested in
    // from the formbag data (values entered by the user in the UI)
    const translations = translatedValues(field);
    
    // find the index for the default language in the translated values from the form
    // ** these are the values we want to replace ** 
    const index = translations.findIndex(lang => isDefaultLanguage(lang.language))
    
    // if the original data contained values for the default language
    // smoosh them back into th translated values data 
    // or if they somehow aren't preent in the translated values, append them to the end
    if (defaultLanguageValues && index >=0) {
      const insertAt = index >= 0 ? index : translations.length;
      translations[index].linkableStringValue.value = defaultLanguageValues
    }

    // return the array containing the form data and the merged original values for default language
    return translations;
  }

  const submit = (formBags: TranslationFormBags<FormValues, LanguageCode>, stop: Stop.Entity) => {
    // Validate all bags
    const valid = validate(formBags)

    // Check if any bags a invalid
    if(!valid) return false;

    const translatedValues = translateValues<Stop.Entity, FormValues, LanguageCode>(stopSchema, stop, LANGUAGES, formBags)

    const translatedTitles = insertOriginalDefaultLanguageValues('title', translatedValues, stop);
    const translatedObjectDescriptions = insertOriginalDefaultLanguageValues('objectDescription', translatedValues, stop);

    // Update Stop with changes.
    gettyUpdateStopRequest({
      ...UpdateStopRequest.defaults,
      stopRef: Stop.mustRef(stop.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: ['audio', 'title', 'objectDescription'],
      },
      audio: {
        type: "mos.translatable.TranslatableMediaValue",
        translatedValues: translatedValues('audio')
      },
      title: {
        type: "mos.translatable.TranslatableLinkableStringValue",
        translatedValues: translatedTitles
      },
      objectDescription: {
        type: "mos.translatable.TranslatableLinkableStringValue",
        translatedValues: translatedObjectDescriptions
      },
    })

    hasSubmitted.current = true

    return false;
  };

  const updateFormBags= (languageCode: LanguageCode) =>  (bag: FormBag<FormValues>) => {
    if (!formBags) return;
    setFormBags({ ...formBags, [languageCode]: bag })
  }

  const title = stopUpdateData && stopUpdateData.stop.title ? getResolvedValue(stopSchema.title.read(stopUpdateData.stop.title), stopUpdateData.resolvedValues || []) : ''
  const producedBy = stopUpdateData && stopUpdateData.stop.producedBy ? getResolvedValue(stopSchema.producedBy.read(stopUpdateData.stop.producedBy), stopUpdateData.resolvedValues || []) : ''
  const stopImage = stopUpdateData && stop && getResolvedValue(stopSchema.image.read(stop.image), stopUpdateData.resolvedValues) as Media.Ref
  const stopImageId = stopImage && stopImage.id
  const imagePath = stopImageId && hostName ? `${hostName}/images/${stopImageId}.jpg?s=crop&h=92&w=92` : ''

  return (
    <>
      <LayoutFullWidthPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title={`Translate stop "${title}"`}
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        subnav={() => (
          <SecondaryNavigation navItems={getStopSubnav(projectId, stopId, tourId)} modified={modified} historyPush={historyPush} >
          </SecondaryNavigation>
        )}
        title={`${title}`}
        content={() => (
          <StatusGuard status={stopUpdate} mode="form">
            <Scroll>
              <Panels>
                {formBags && LANGUAGES.map((value) => {
                  const languageCode: LanguageCode = value.languageCode as LanguageCode
                  return (
                    <TranslationForm key={languageCode} language={value} formBag={formBags[languageCode]} setFormBag={updateFormBags(languageCode)} />
                  )
                })}
              </Panels>
            </Scroll>
          </StatusGuard>
        )}
        footer={() => formBags && stop && (
          <FooterEditEntity
            entity={stopUpdate}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit(formBags, stop);
              return false;
            }}
          />
        )}
      />
    </>
  )
}

export const StopTranslations = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => pick(store.getty, 'stopUpdate'),
  {
    ...pick(actionCreators, 'gettyGetStopRequest', 'gettyUpdateStopRequest', 'gettyUpdateStopReset'),
    ...pick(sharedActionCreators, 'toastNotification'),
  },
)(StopTranslationsView);
